import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaCoins, FaTimes } from 'react-icons/fa';
import { useAds } from '../context/AdContext.jsx';
import { useCoins } from '../hooks/useCoins.jsx';

// Reward values per wheel segment (clockwise from the top).
const SEGMENTS = [50, 20, 100, 30, 75, 20, 150, 40];
const SEG = 360 / SEGMENTS.length;
const COLORS = ['#7c3aed', '#a21caf', '#6c5ce7', '#c026d3'];

const conic = `conic-gradient(${SEGMENTS.map(
  (_, i) => `${COLORS[i % COLORS.length]} ${i * SEG}deg ${(i + 1) * SEG}deg`,
).join(', ')})`;

// Daily spin: spin the wheel for a free reward, then OPTIONALLY watch a
// rewarded ad to double it. The ad is never shown automatically.
export default function SpinWheel({ onClose }) {
  const { showRewarded } = useAds();
  const { addCoins } = useCoins();
  const [phase, setPhase] = useState('ready'); // ready | spinning | result
  const [rotation, setRotation] = useState(0);
  const [won, setWon] = useState(0);
  const [doubled, setDoubled] = useState(false);
  const [busy, setBusy] = useState(false);
  const spinTo = useRef(null);

  // Cancel the pending spin-resolve if the modal closes mid-spin (so coins are
  // never awarded for a spin the user dismissed).
  useEffect(() => () => clearTimeout(spinTo.current), []);

  // Lock the page scroll while the spin modal is open so the background can't
  // scroll behind the (portaled) overlay.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const spin = () => {
    if (phase !== 'ready') return;
    const idx = Math.floor(Math.random() * SEGMENTS.length);
    // Land the chosen segment's centre under the top pointer (+5 full turns).
    const target = 5 * 360 - (idx * SEG + SEG / 2);
    setRotation(target);
    setPhase('spinning');
    spinTo.current = setTimeout(() => {
      const amount = SEGMENTS[idx];
      setWon(amount);
      addCoins(amount);
      setPhase('result');
    }, 4200);
  };

  const handleDouble = async () => {
    setBusy(true);
    const { viewed } = await showRewarded();
    if (viewed) {
      addCoins(won);
      setDoubled(true);
    }
    setBusy(false);
  };

  return createPortal(
    <div className="fade-in fixed inset-0 z-[90] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-6 overflow-y-auto">
      <div className="relative w-full max-w-sm rounded-[2.5rem] bg-slate-900 border border-white/10 p-8 text-center pop-in shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 text-slate-400 hover:text-white"
        >
          <FaTimes />
        </button>

        <h2 className="text-2xl font-black uppercase tracking-wide text-white">
          Daily <span className="text-gradient-brand">Spin</span>
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          {phase === 'result' ? 'Here are your coins!' : 'Spin the wheel — it’s free!'}
        </p>

        {/* Wheel */}
        <div className="relative mx-auto my-7 w-64 h-64">
          {/* pointer */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-amber-400 drop-shadow" />

          {/* rotating wheel */}
          <div
            className="absolute inset-0 rounded-full border-4 border-white/20 shadow-[0_0_40px_rgba(124,58,237,0.5)]"
            style={{
              background: conic,
              transform: `rotate(${rotation}deg)`,
              transition: phase === 'spinning' ? 'transform 4s cubic-bezier(0.17,0.67,0.12,0.99)' : 'none',
            }}
          >
            {SEGMENTS.map((v, i) => {
              const a = ((i * SEG + SEG / 2) * Math.PI) / 180;
              const r = 84;
              const x = 128 + r * Math.sin(a);
              const y = 128 - r * Math.cos(a);
              return (
                <div
                  key={i}
                  className="absolute flex flex-col items-center text-white font-black drop-shadow"
                  style={{
                    left: x,
                    top: y,
                    transform: `translate(-50%, -50%) rotate(${i * SEG + SEG / 2}deg)`,
                  }}
                >
                  <FaCoins className="text-amber-300 text-xs" />
                  <span className="text-sm leading-none mt-0.5">{v}</span>
                </div>
              );
            })}
          </div>

          {/* hub */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center">
            <FaCoins className="text-amber-500 text-xl" />
          </div>
        </div>

        {/* Controls */}
        {phase !== 'result' ? (
          <button
            onClick={spin}
            disabled={phase === 'spinning'}
            className="btn-3d w-full py-4 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-70"
          >
            {phase === 'spinning' ? 'Spinning…' : 'Spin Now'}
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-lg font-black text-white">
              You won{' '}
              <span className="text-amber-400">
                {doubled ? won * 2 : won} coins
              </span>
              {doubled && <span className="text-emerald-400"> (2×!)</span>}
            </p>

            {!doubled && (
              <button
                onClick={handleDouble}
                disabled={busy}
                className="btn-3d relative w-full py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-widest text-sm disabled:opacity-70"
              >
                <span className="absolute top-1.5 right-3 text-[8px] font-bold text-white/70">
                  Watch Ad
                </span>
                💎 Double to {won * 2}
              </button>
            )}

            <button
              onClick={onClose}
              className="btn-3d w-full py-4 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-black uppercase tracking-widest text-sm"
            >
              Collect
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
